import type { IconName } from "@/components/ui/icons.js";

export interface NavEntry {
  path: string;
  label: string;
  icon: IconName;
  adminOnly?: boolean;
  /** Future module from the product vision — shown disabled, never as production-ready. */
  planned?: boolean;
  /**
   * Modul existiert, lebt aber als Tab IM Projekt (kein eigener globaler Ort).
   * Wird als klickbarer Verweis auf /projects gerendert, Badge "im Projekt",
   * ohne eigenen Active-State — ehrlich statt "geplant" auf Live-Funktionen.
   */
  inProject?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavEntry[];
}

/**
 * Sidebar navigation. Live routes map to real, implemented pages.
 * `planned: true` entries communicate the roadmap (per the mockup) without
 * pretending the feature exists — they are not clickable.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Planung",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: "dashboard" },
      { path: "/projects", label: "Projekte", icon: "projects" },
      { path: "/customers", label: "Kunden", icon: "customers" },
    ],
  },
  {
    label: "Fachmodule",
    items: [
      // Diese Module SIND live — sie leben als Tabs im Projekt (E2E-verifiziert
      // 2026-06-10: Dachplaner, Elektroplanung/PV-Auslegung, Speicher B5,
      // Simulation, Wirtschaftlichkeit, Konzept, Dokumente). Kein "geplant"
      // auf funktionierenden Features; Verweis öffnet die Projektliste.
      { path: "/projects", label: "Dach-Designer", icon: "roof", inProject: true },
      { path: "/projects", label: "PV-Auslegung", icon: "pv", inProject: true },
      { path: "/projects", label: "Speicher", icon: "storage", inProject: true },
      { path: "/projects", label: "Simulation", icon: "simulation", inProject: true },
      { path: "/projects", label: "Wirtschaftlichkeit", icon: "economics", inProject: true },
      { path: "/projects", label: "Konzepte", icon: "concepts", inProject: true },
      { path: "/projects", label: "Dokumente", icon: "documents", inProject: true },
      // Ehrlich geplant: kein Monitoring-Feature vorhanden (Fronius = Phase 5).
      { path: "/monitoring", label: "Monitoring", icon: "monitoring", planned: true },
    ],
  },
  {
    label: "Daten",
    items: [
      { path: "/kalkulation", label: "Schnellkalkulation", icon: "economics" },
      { path: "/knowledge", label: "Wissensdatenbank", icon: "knowledge" },
      { path: "/components", label: "Komponenten", icon: "components" },
      { path: "/foerderungen", label: "Förderungen", icon: "funding" },
    ],
  },
  {
    label: "System",
    items: [
      { path: "/users", label: "Benutzer", icon: "user" },
      { path: "/audit", label: "Audit-Log", icon: "audit" },
      { path: "/admin", label: "Administration", icon: "admin", adminOnly: true },
    ],
  },
];

const ALL_ENTRIES: NavEntry[] = NAV_GROUPS.flatMap((g) => g.items);

/** Resolve a page title from the current path (longest matching live route). */
export function routeTitle(pathname: string): string {
  let best: NavEntry | undefined;
  for (const e of ALL_ENTRIES) {
    if (pathname === e.path || pathname.startsWith(e.path + "/")) {
      if (!best || e.path.length > best.path.length) best = e;
    }
  }
  if (best) return best.label;
  if (pathname.startsWith("/projects")) return "Projekte";
  return "EnergyOS";
}
